(() => {
  const SHORT_TITLE_SELECTOR = [
    '.product-card-wrapper .card__heading a',
    '.predictive-search__item-heading'
  ].join(',');

  function shortenProductTitles(root = document) {
    const elements = root.matches?.(SHORT_TITLE_SELECTOR)
      ? [root]
      : [...(root.querySelectorAll?.(SHORT_TITLE_SELECTOR) || [])];

    elements.forEach((element) => {
      const fullTitle = element.dataset.sulindaFullTitle || element.textContent.trim();
      if (!fullTitle || !fullTitle.includes('|')) return;

      const shortTitle = fullTitle.split('|')[0].trim();
      if (!shortTitle || shortTitle === element.textContent.trim()) return;

      element.dataset.sulindaFullTitle = fullTitle;
      element.setAttribute('title', fullTitle);
      element.textContent = shortTitle;
    });
  }

  const storeContext = () => document.getElementById('SulindaStoreContext');
  const freeShippingThreshold = 14900;
  let cartUpdateTimer;
  let cartUpdateInFlight = false;

  function formatAED(cents) {
    try {
      return new Intl.NumberFormat(document.documentElement.lang || 'en-AE', {
        style: 'currency',
        currency: 'AED',
        minimumFractionDigits: 0,
        maximumFractionDigits: 2
      }).format(cents / 100);
    } catch (_) {
      return `AED ${(cents / 100).toFixed(2)}`;
    }
  }

  function removeFreeShippingBars() {
    document.querySelectorAll('.sulinda-free-shipping').forEach((bar) => bar.remove());
  }

  function ensureFreeShippingBar(container, key) {
    if (!container) return null;
    let bar = container.querySelector(`.sulinda-free-shipping[data-location="${key}"]`);
    if (bar) return bar;

    bar = document.createElement('div');
    bar.className = 'sulinda-free-shipping';
    bar.dataset.location = key;
    bar.innerHTML = `
      <p class="sulinda-free-shipping__text"></p>
      <div class="sulinda-free-shipping__track" aria-hidden="true">
        <div class="sulinda-free-shipping__fill"></div>
      </div>
    `;

    const totals = container.querySelector('.totals');
    if (totals) container.insertBefore(bar, totals);
    else container.prepend(bar);
    return bar;
  }

  function updateBar(bar, totalPrice) {
    if (!bar) return;
    const remaining = Math.max(0, freeShippingThreshold - totalPrice);
    const progress = Math.min(100, Math.max(0, (totalPrice / freeShippingThreshold) * 100));
    const isArabic = (document.documentElement.lang || '').toLowerCase().startsWith('ar');
    const message = remaining > 0
      ? (isArabic
          ? `متبقي ${formatAED(remaining)} للحصول على الشحن المجاني داخل الإمارات.`
          : `You're ${formatAED(remaining)} away from free UAE shipping.`)
      : (isArabic
          ? 'تم تفعيل الشحن المجاني داخل الإمارات.'
          : 'Free UAE shipping unlocked.');

    const text = bar.querySelector('.sulinda-free-shipping__text');
    const fill = bar.querySelector('.sulinda-free-shipping__fill');
    if (text && text.textContent !== message) text.textContent = message;
    const nextWidth = `${progress.toFixed(1)}%`;
    if (fill && fill.style.width !== nextWidth) fill.style.width = nextWidth;
  }

  async function refreshFreeShipping() {
    const context = storeContext();
    if (!context || context.dataset.country !== 'AE') {
      removeFreeShippingBars();
      return;
    }

    if (cartUpdateInFlight) return;
    cartUpdateInFlight = true;

    try {
      const root = window.Shopify?.routes?.root || '/';
      const response = await fetch(`${root}cart.js`, {
        credentials: 'same-origin',
        headers: { Accept: 'application/json' }
      });
      if (!response.ok) return;
      const cart = await response.json();

      if (cart.currency !== 'AED' || cart.item_count === 0) {
        removeFreeShippingBars();
        return;
      }

      const drawerContainer = document.querySelector('.drawer__footer > .cart-drawer__footer');
      const cartContainer = document.querySelector('#main-cart-footer .cart__blocks .js-contents');
      updateBar(ensureFreeShippingBar(drawerContainer, 'drawer'), cart.total_price);
      updateBar(ensureFreeShippingBar(cartContainer, 'cart-page'), cart.total_price);
    } catch (_) {
      /* Shipping rules at checkout remain authoritative if the cart helper cannot load. */
    } finally {
      cartUpdateInFlight = false;
    }
  }

  function queueCartRefresh() {
    window.clearTimeout(cartUpdateTimer);
    cartUpdateTimer = window.setTimeout(refreshFreeShipping, 180);
  }

  document.addEventListener('DOMContentLoaded', () => {
    shortenProductTitles();
    queueCartRefresh();

    const observer = new MutationObserver((mutations) => {
      let cartChanged = false;
      mutations.forEach((mutation) => {
        mutation.addedNodes.forEach((node) => {
          if (node.nodeType !== Node.ELEMENT_NODE) return;
          shortenProductTitles(node);
          if (
            node.matches?.('cart-drawer, #main-cart-footer, .cart-item, .totals') ||
            node.querySelector?.('cart-drawer, #main-cart-footer, .cart-item, .totals') ||
            node.closest?.('cart-drawer, #main-cart-footer')
          ) {
            cartChanged = true;
          }
        });

        if (mutation.target?.closest?.('cart-drawer, #main-cart-footer')) cartChanged = true;
      });
      if (cartChanged) queueCartRefresh();
    });

    observer.observe(document.body, { childList: true, subtree: true });
  });
})();

/* Arabic-only cart copy and currency cleanup. Native checkout remains controlled by Shopify. */
(() => {
  const isArabic = () => (document.documentElement.lang || '').toLowerCase().startsWith('ar');
  if (!isArabic()) return;

  const CART_SCOPE = '#CartDrawer, cart-drawer, #main-cart-items, #main-cart-footer, main-cart-items, main-cart-footer';
  const MONEY_SELECTOR = [
    '.price',
    '.totals__total-value',
    '.cart-item__old-price',
    '.cart-item__final-price',
    '.cart-item__price-wrapper',
    '.unit-price',
    '.sulinda-free-shipping__text'
  ].join(',');

  function normalizeCurrencyText(value) {
    return value
      .replace(/Dhs\.?[\u200e\u200f\s]*([\d,]+(?:\.\d{1,2})?)(?:[\u200e\u200f\s]*AED)?/gi, '$1 د.إ')
      .replace(/\bAED[\u200e\u200f\s]*([\d,]+(?:\.\d{1,2})?)/gi, '$1 د.إ')
      .replace(/([\d,]+(?:\.\d{1,2})?)[\u200e\u200f\s]*AED\b/gi, '$1 د.إ')
      .replace(/([\d,]+(?:\.\d{1,2})?)[\u200e\u200f\s]*د\.?[\u200e\u200f\s]*إ\.?/g, '$1 د.إ');
  }

  function normalizeMoneyElement(element) {
    const walker = document.createTreeWalker(element, NodeFilter.SHOW_TEXT);
    const nodes = [];
    while (walker.nextNode()) nodes.push(walker.currentNode);
    let changed = false;
    nodes.forEach((node) => {
      const current = node.nodeValue || '';
      const next = normalizeCurrencyText(current);
      if (next !== current) {
        node.nodeValue = next;
        changed = true;
      }
    });
    if (changed && !element.classList.contains('sulinda-free-shipping__text')) {
      element.setAttribute('dir', 'ltr');
      element.style.unicodeBidi = 'isolate';
    }
  }

  function normalizeArabicCart() {
    document.querySelectorAll(CART_SCOPE).forEach((root) => {
      root.querySelectorAll('.drawer__heading, h1, h2').forEach((heading) => {
        if ((heading.textContent || '').trim() === 'عربة التسوق الخاصة بك') {
          heading.textContent = 'سلة التسوق';
        }
      });
      root.querySelectorAll(MONEY_SELECTOR).forEach(normalizeMoneyElement);
    });
  }

  let queued = false;
  function queueNormalize() {
    if (queued) return;
    queued = true;
    requestAnimationFrame(() => {
      queued = false;
      normalizeArabicCart();
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', normalizeArabicCart);
  } else {
    normalizeArabicCart();
  }

  const observer = new MutationObserver(queueNormalize);
  observer.observe(document.documentElement, { childList: true, subtree: true, characterData: true });
  document.addEventListener('shopify:section:load', queueNormalize);
})();
